import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FileText, Globe, Megaphone, Speaker } from "lucide-react";
import { Campaign, SourceTypes } from "../types/types";

export type TabType = "Source Management" | "Forms" | "Campaign Management";

export const leadSourceTabs: Tab[] = [
  {
    label: "Source Management",
    Icon: Globe,
    value: "Source Management" as TabType,
  },
  {
    label: "Forms",
    Icon: FileText,
    value: "Forms" as TabType,
  },
  {
    label: "Campaign Management",
    Icon: Megaphone,
    value: "Campaign Management" as TabType,
  },
];

export interface Tab {
  label: TabType;
  Icon: typeof Globe | typeof Speaker;
  value: TabType;
}

export type CampaignOrigin = "CUSTOM" | "META" | "SYSTEM";
export type ActiveStatusFilter = "all" | "active" | "inactive";

export interface SourceFiltersState {
  types: SourceTypes[];
  status: ActiveStatusFilter;
}

export interface CampaignFiltersState {
  origin: CampaignOrigin | "";
  status: ActiveStatusFilter;
  leadSourceIds: number[];
  /**
   * Lead `createdAt` window (yyyy-mm-dd, IST) that scopes each campaign card's
   * "leads generated" count. Empty strings = all-time. Does NOT hide campaigns.
   */
  startDate: string;
  endDate: string;
}

export const initialSourceFilters: SourceFiltersState = {
  types: [],
  status: "all",
};

export const initialCampaignFilters: CampaignFiltersState = {
  origin: "",
  status: "all",
  leadSourceIds: [],
  startDate: "",
  endDate: "",
};

/**
 * The Web Form builder popup — ONE instance, rendered from SourcePage and
 * opened from the Add Source drawer, the Forms tab and WEB_FORM source cards.
 * `sourceId` null = create mode (`prefill` carries the drawer's name/description).
 */
export interface WebFormBuilderState {
  open: boolean;
  sourceId: number | null;
  prefill: { name?: string; description?: string } | null;
}

/** What a new source starts as when another screen opens Add Source. */
export type AddSourcePreset = "whatsapp-api" | null;

export interface UserState {
  isAddSource: boolean;
  /** "whatsapp-api" = a Webhook source with "Use as WhatsApp" already on (WhatsApp → Add Account). */
  addSourcePreset: AddSourcePreset;
  isAddCampaign: boolean;
  isViewCampaign: boolean;
  viewCurrentCampaign: Campaign | null;
  isEditCampaign: boolean;
  editCurrentCampaign: Campaign | null;

  currentTab: TabType;

  isAddCall: boolean;

  // Search + filter UI state (per-tab)
  searchQuery: string;
  isFilterOpen: boolean;
  sourceFilters: SourceFiltersState;
  campaignFilters: CampaignFiltersState;

  webFormBuilder: WebFormBuilderState;
  /** Full-screen call-routing setup (a Custom source with config.callRouting). */
  callRoutingBuilder: CallRoutingBuilderState;
}

export interface CallRoutingBuilderState {
  open: boolean;
  sourceId: number | null;
  prefill: { name?: string; description?: string } | null;
}

const initialState: UserState = {
  isAddSource: false,
  addSourcePreset: null,
  isAddCampaign: false,
  isViewCampaign: false,
  viewCurrentCampaign: null,
  isEditCampaign: false,
  editCurrentCampaign: null,
  currentTab: "Source Management",
  isAddCall: false,

  searchQuery: "",
  isFilterOpen: false,
  sourceFilters: initialSourceFilters,
  campaignFilters: initialCampaignFilters,

  webFormBuilder: { open: false, sourceId: null, prefill: null },
  callRoutingBuilder: { open: false, sourceId: null, prefill: null },
};

const sourceSlice = createSlice({
  name: "source-functional",
  initialState,
  reducers: {
    setActiveSourceTab(state, action: PayloadAction<TabType>) {
      state.currentTab = action.payload;
      // Clear search when switching tabs so the placeholder/results match the new tab
      state.searchQuery = "";
    },
    setIsAddSource(state, action: PayloadAction<boolean>) {
      state.isAddSource = action.payload;
      // A preset is for the one drawer it opened; the next "+ Add Source" starts blank.
      state.addSourcePreset = null;
    },
    /** Opens Add Source on the Sources tab, optionally pre-set (WhatsApp → Add Account). */
    openAddSource(state, action: PayloadAction<AddSourcePreset>) {
      state.currentTab = "Source Management";
      state.searchQuery = "";
      state.isAddSource = true;
      state.addSourcePreset = action.payload;
    },
    setIsAddCampaign(state, action: PayloadAction<boolean>) {
      state.isAddCampaign = action.payload;
    },
    setIsViewCampaign(state, action: PayloadAction<boolean>) {
      state.isViewCampaign = action.payload;
    },
    setViewCurrentCampaign(state, action: PayloadAction<Campaign | null>) {
      state.viewCurrentCampaign = action.payload;
    },
    setIsEditCampaign(state, action: PayloadAction<boolean>) {
      state.isEditCampaign = action.payload;
    },
    setEditCurrentCampaign(state, action: PayloadAction<Campaign | null>) {
      state.editCurrentCampaign = action.payload;
    },

    setSearchQuery(state, action: PayloadAction<string>) {
      state.searchQuery = action.payload;
    },
    setIsFilterOpen(state, action: PayloadAction<boolean>) {
      state.isFilterOpen = action.payload;
    },
    setSourceFilters(
      state,
      action: PayloadAction<Partial<SourceFiltersState>>
    ) {
      state.sourceFilters = { ...state.sourceFilters, ...action.payload };
    },
    setCampaignFilters(
      state,
      action: PayloadAction<Partial<CampaignFiltersState>>
    ) {
      state.campaignFilters = { ...state.campaignFilters, ...action.payload };
    },
    resetSourceFilters(state) {
      state.sourceFilters = initialSourceFilters;
    },
    resetCampaignFilters(state) {
      state.campaignFilters = initialCampaignFilters;
    },
    openWebFormBuilder(
      state,
      action: PayloadAction<{
        sourceId?: number | null;
        prefill?: WebFormBuilderState["prefill"];
      }>
    ) {
      state.webFormBuilder = {
        open: true,
        sourceId: action.payload.sourceId ?? null,
        prefill: action.payload.prefill ?? null,
      };
    },
    closeWebFormBuilder(state) {
      state.webFormBuilder = { open: false, sourceId: null, prefill: null };
    },
    openCallRoutingBuilder(
      state,
      action: PayloadAction<{
        sourceId?: number | null;
        prefill?: CallRoutingBuilderState["prefill"];
      }>
    ) {
      state.callRoutingBuilder = {
        open: true,
        sourceId: action.payload.sourceId ?? null,
        prefill: action.payload.prefill ?? null,
      };
    },
    closeCallRoutingBuilder(state) {
      state.callRoutingBuilder = { open: false, sourceId: null, prefill: null };
    },
  },
});

export const {
  setIsAddSource,
  openAddSource,
  setIsAddCampaign,
  setActiveSourceTab,
  setIsViewCampaign,
  setViewCurrentCampaign,
  setIsEditCampaign,
  setEditCurrentCampaign,
  setSearchQuery,
  setIsFilterOpen,
  setSourceFilters,
  setCampaignFilters,
  resetSourceFilters,
  resetCampaignFilters,
  openWebFormBuilder,
  closeWebFormBuilder,
  openCallRoutingBuilder,
  closeCallRoutingBuilder,
} = sourceSlice.actions;

export default sourceSlice.reducer;
